import React from "react";
import { BadgeDollarSign, Wallet } from "lucide-react";
import Tooltip from "./Tooltip";
import { TRADING_TOKEN } from "../configs/config";

interface Position {
  composite_key: number;
  account: string;
  outcome_id: number;
  shares: number;
  invested?: number;
  market_id?: number;
  outcome_name?: string;
}

interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
}

interface MarketPositions {
  market_id: number;
  market: any;
  positions: Position[];
  outcomes: Outcome[];
  myInvested?: number;
  claimedShares?: number;
  unclaimedShares?: number;
}

interface PortfolioPositionCardProps {
  marketPosition: MarketPositions;
  onClaim: (marketId: number) => void;
}

const formatShares = (shares: number): string => {
  if (!Number.isFinite(shares)) {
    return "~0.00";
  }

  return `~${shares.toFixed(2)}`;
};

const truncateHoldingLabel = (label: string): string => {
  if (label.length <= 15) {
    return label;
  }

  return `${label.slice(0, 15)}...`;
};

const isWinningResolvedPosition = (
  market: any,
  position: Position,
): boolean => {
  if (!market?.resolved || position.shares <= 0) {
    return false;
  }

  if (market.version === 3) {
    const label = position.outcome_name || "";
    if (label.endsWith(" YES")) {
      return position.outcome_id === market.outcome;
    }
    if (label.endsWith(" NO")) {
      return position.outcome_id !== market.outcome;
    }
  }

  return position.outcome_id === market.outcome;
};

const PortfolioPositionCard: React.FC<PortfolioPositionCardProps> = ({
  marketPosition,
  onClaim,
}) => {
  const { market, market_id, positions, outcomes } = marketPosition;
  const activePositions = positions.filter((position) => position.shares > 0);
  const isResolved = Boolean(market.resolved);
  const isRefundable = Boolean(market.is_refundable);
  const hasClaimed = (marketPosition.claimedShares ?? 0) > 0;
  const hasWinningPosition = activePositions.some((position) =>
    isWinningResolvedPosition(market, position),
  );
  const isClaimEnabled =
    !hasClaimed && (isRefundable || (isResolved && hasWinningPosition));
  const statusLabel = isResolved
    ? "Resolved"
    : isRefundable
      ? "Refundable"
      : "Live";
  const statusClass = isResolved
    ? "resolved"
    : isRefundable
      ? "refundable"
      : "live";
  const claimLabel = hasClaimed
    ? isRefundable
      ? "Refund Claimed"
      : "Winnings Claimed"
    : isRefundable
      ? "Claim Refund"
    : isResolved
      ? hasWinningPosition
        ? "Claim Winnings"
        : "No Winnings"
      : "Claim";
  const winningOutcomeName = isResolved
    ? outcomes.find((outcome) => outcome.outcome_id === market.outcome)?.name ||
      "Unknown"
    : null;
  const footerClass = isClaimEnabled
    ? "portfolio-position-card__footer portfolio-position-card__footer--claimable"
    : "portfolio-position-card__footer portfolio-position-card__footer--inactive";

  return (
    <article className="portfolio-position-card">
      <header className="portfolio-position-card__header">
        <div className="portfolio-position-card__title-block">
          <h4>{market.question}</h4>
        </div>
        <div className="portfolio-position-card__meta">
          <span
            className={`portfolio-position-card__status portfolio-position-card__status--${statusClass}`}
          >
            {statusLabel}
          </span>
          <span className="portfolio-position-card__market-id">#{market.id}</span>
        </div>
      </header>

      <section className="portfolio-position-card__holdings">
        <div className="portfolio-position-card__section-label">My Holdings</div>
        <div className="portfolio-position-card__holdings-list">
          {activePositions.length > 0 ? (
            activePositions.map((position) => (
              <div
                key={position.composite_key}
                className="portfolio-position-card__holding-row"
              >
                <div className="portfolio-position-card__holding-title-row">
                  <span
                    className="portfolio-position-card__holding-name"
                    title={position.outcome_name || `Outcome ${position.outcome_id}`}
                  >
                    {truncateHoldingLabel(
                      position.outcome_name || `Outcome ${position.outcome_id}`,
                    )}
                  </span>
                </div>
                <div className="portfolio-position-card__holding-stats-row">
                  <div className="portfolio-position-card__holding-stat">
                    <span
                      className="portfolio-position-card__holding-icon"
                      aria-hidden="true"
                    >
                      <BadgeDollarSign size={18} strokeWidth={2.1} />
                    </span>
                    <div className="portfolio-position-card__holding-metric">
                      <span className="portfolio-position-card__holding-invested">
                        <span className="portfolio-position-card__holding-value">
                          {position.invested ?? 0}
                        </span>
                        <span className="portfolio-position-card__holding-unit">
                          {TRADING_TOKEN}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="portfolio-position-card__holding-stat">
                    <span
                      className="portfolio-position-card__holding-icon"
                      aria-hidden="true"
                    >
                      <Wallet size={18} strokeWidth={2.1} />
                    </span>
                    <div className="portfolio-position-card__holding-metric">
                      <span className="portfolio-position-card__holding-shares">
                        <span className="portfolio-position-card__holding-value">
                          {formatShares(position.shares)}
                        </span>
                        <span className="portfolio-position-card__holding-unit">
                          shares
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="portfolio-position-card__holdings-empty">
              No active holdings
            </div>
          )}
        </div>
      </section>

      {(winningOutcomeName || isRefundable) && (
        <div className="portfolio-position-card__summary">
          {winningOutcomeName ? (
          <div className="portfolio-position-card__summary-panel portfolio-position-card__summary-panel--accent">
            <span className="portfolio-position-card__summary-label">
              Winning outcome
            </span>
            <strong>{winningOutcomeName}</strong>
          </div>
        ) : isRefundable ? (
          <div className="portfolio-position-card__summary-panel portfolio-position-card__summary-panel--accent">
            <span className="portfolio-position-card__summary-label">
              Refund status
            </span>
            <strong>Refund available</strong>
          </div>
          ) : null}
        </div>
      )}

      <div className={footerClass}>
        <Tooltip
          text={
            hasClaimed
              ? isRefundable
                ? "This refund has already been claimed."
                : "These winnings have already been claimed."
            : isClaimEnabled
              ? isRefundable && !isResolved
                ? "This market is refundable. Claim back your active invested amount."
                : "This market is resolved. Claim any eligible winnings from your live position."
              : isResolved
                ? "This market resolved against your current live holdings."
              : "Claim becomes available only after the market resolves or is marked refundable."
          }
          position="top"
        >
          <button
            type="button"
            className="portfolio-position-card__claim-button"
            disabled={!isClaimEnabled}
            onClick={() => onClaim(market_id)}
          >
            {claimLabel}
          </button>
        </Tooltip>
      </div>
    </article>
  );
};

export default PortfolioPositionCard;
